import { useEffect, useMemo, useState } from 'react';
import '../../pages/PowerMeterContractDetail/PowerMeterContractDetail.css';
import SearchBox from '../SearchBox/SearchBox';
import Pagination from '../Pagination/Pagination';
import {
  getOpmContractNumber,
  getDataEndDateString,
  getEndDateHighlightClass,
  matchesConnectFilter,
  ConnectStatusBadge,
} from './OpmContractListHelpers.jsx';

const DEFAULT_PAGE_SIZE = 10;

/**
 * DR 자원에 시스템 등록된 OPM 계약 목록 (필터·검색·페이지네이션, 행 선택 없음)
 */
const SystemRegisteredContractTable = ({
  opmList = [],
  pageSize = DEFAULT_PAGE_SIZE,
  listTitle = '시스템 등록 목록',
}) => {
  const [contractListPage, setContractListPage] = useState(1);
  const [contractSearchApplied, setContractSearchApplied] = useState('');
  const [connectFilter, setConnectFilter] = useState('all');

  const filteredOpmList = useMemo(() => {
    let list = Array.isArray(opmList) ? opmList : [];
    if (connectFilter !== 'all') {
      list = list.filter((row) => matchesConnectFilter(row, connectFilter));
    }
    const q = contractSearchApplied.trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const num = String(getOpmContractNumber(row)).toLowerCase();
        return num.includes(q);
      });
    }
    return list;
  }, [opmList, contractSearchApplied, connectFilter]);

  const contractTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredOpmList.length / pageSize)),
    [filteredOpmList.length, pageSize]
  );

  const pagedOpmList = useMemo(() => {
    const start = (contractListPage - 1) * pageSize;
    return filteredOpmList.slice(start, start + pageSize);
  }, [filteredOpmList, contractListPage, pageSize]);

  useEffect(() => {
    setContractListPage((p) => Math.min(p, contractTotalPages));
  }, [contractTotalPages]);

  useEffect(() => {
    setContractListPage(1);
  }, [opmList]);

  const handleContractSearch = (value) => {
    setContractSearchApplied(String(value ?? '').trim());
    setContractListPage(1);
  };

  const handleConnectFilterChange = (next) => {
    setConnectFilter(next);
    setContractListPage(1);
  };

  return (
    <div className="opm-linkage-column-inner flex flex-col gap-8">
      <div className="flex align-center justify-between flex-wrap gap-5">
        <div className="flex align-center gap-5 flex-wrap">
          <h3 className="text-b1-700 text-base">{listTitle}</h3>
          <p className="text-b2-400 text-gray-600">
            ({filteredOpmList.length}
            {(contractSearchApplied.trim() || connectFilter !== 'all') &&
            opmList.length !== filteredOpmList.length
              ? ` / ${opmList.length}`
              : ''}
            )
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-8">
        <div className="opm-connect-filter" role="group" aria-label="OPM데이터제공상태 필터">
          <span className="opm-connect-filter__label text-b2-400 text-gray-600">OPM 데이터 제공 상태 필터</span>
          <div className="opm-connect-filter__buttons">
            <button
              type="button"
              className={`opm-connect-filter__btn${connectFilter === 'all' ? ' opm-connect-filter__btn--active' : ''}`}
              onClick={() => handleConnectFilterChange('all')}
            >
              전체
            </button>
            <button
              type="button"
              className={`opm-connect-filter__btn${connectFilter === 'connected' ? ' opm-connect-filter__btn--active' : ''}`}
              onClick={() => handleConnectFilterChange('connected')}
            >
              제공
            </button>
            <button
              type="button"
              className={`opm-connect-filter__btn${connectFilter === 'disconnected' ? ' opm-connect-filter__btn--active' : ''}`}
              onClick={() => handleConnectFilterChange('disconnected')}
            >
              미제공
            </button>
          </div>
        </div>
        <SearchBox
          placeholder="계약번호 검색 (부분 일치)"
          showFilter={false}
          onSearch={handleContractSearch}
          defaultValue={contractSearchApplied}
        />
      </div>
      {opmList.length === 0 ? (
        <p className="text-b2-400 text-gray-600">등록된 계약번호가 없습니다.</p>
      ) : filteredOpmList.length === 0 ? (
        <p className="text-b2-400 text-gray-600">필터 조건에 맞는 계약이 없습니다.</p>
      ) : (
        <div className="opm-contract-list">
          <div className="primary-table opm-contract-table">
            <table>
              <thead>
                <tr>
                  <th>계약번호</th>
                  <th>OPM데이터제공상태</th>
                  <th>정보제공시작일</th>
                  <th>정보제공종료일</th>
                </tr>
              </thead>
              <tbody>
                {pagedOpmList.map((row) => {
                  const num = getOpmContractNumber(row);
                  return (
                    <tr key={num || JSON.stringify(row)}>
                      <td>{num || '—'}</td>
                      <td>
                        <ConnectStatusBadge connect={row.ConnectStatus} />
                      </td>
                      <td>{row.DataStartDate ?? row.dataStartDate ?? '—'}</td>
                      <td className={getEndDateHighlightClass(getDataEndDateString(row))}>
                        {getDataEndDateString(row) ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {contractTotalPages > 1 && (
            <div className="opm-contract-list-pagination">
              <Pagination
                currentPage={contractListPage}
                totalPages={contractTotalPages}
                onPageChange={setContractListPage}
                maxVisiblePages={10}
                size="small"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemRegisteredContractTable;
